import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Inbox, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { apiError, authApi } from "@/lib/auth";
import { eur } from "@/lib/pricing";
import { STATUSES } from "@/lib/orderStatus";
import AdminShell from "@/components/admin/AdminShell";
import OrderRow from "@/components/admin/OrderRow";
import OrderDetail from "@/components/admin/OrderDetail";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tutti");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authApi.get("/admin/orders");
      setOrders(res.data.orders);
    } catch (e) {
      toast.error(apiError(e, "Impossibile caricare i preventivi."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return orders.filter(
      (o) =>
        (filter === "tutti" || o.status === filter) &&
        (!s || o.code.toLowerCase().includes(s) || o.customer.company.toLowerCase().includes(s) || o.customer.contact_name.toLowerCase().includes(s))
    );
  }, [orders, filter, q]);

  const selected = orders.find((o) => o.id === selectedId) || null;
  const stats = useMemo(() => {
    const count = (k) => orders.filter((o) => o.status === k).length;
    const confirmedValue = orders.filter((o) => o.status === "confermato").reduce((a, o) => a + o.subtotal, 0);
    return { nuovi: count("nuovo"), contattati: count("contattato"), confermati: count("confermato"), confirmedValue };
  }, [orders]);

  const onUpdated = (o) => setOrders((prev) => prev.map((x) => (x.id === o.id ? o : x)));

  return (
    <AdminShell>
      <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta">Area fondatori</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl" data-testid="admin-orders-title">Preventivi ricevuti</h1>
        </div>
        <div className="grid grid-cols-2 gap-px border border-black/10 bg-black/10 sm:grid-cols-4" data-testid="admin-stats">
          {[
            ["Nuovi", stats.nuovi, "admin-stat-nuovi"],
            ["Contattati", stats.contattati, "admin-stat-contattati"],
            ["Confermati", stats.confermati, "admin-stat-confermati"],
            ["Valore confermato", eur(stats.confirmedValue), "admin-stat-value"],
          ].map(([label, val, tid]) => (
            <div key={label} className="bg-white px-5 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45">{label}</p>
              <p className="mt-1 font-display text-xl font-extrabold tracking-tight" data-testid={tid}>{val}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
          {[{ key: "tutti", label: "Tutti" }, ...STATUSES].map((s) => (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors duration-200 ${
                filter === s.key ? "bg-ink text-white" : "border border-black/15 bg-white text-ink/60 hover:border-ink hover:text-ink"
              }`}
              data-testid={`admin-filter-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <div className="relative w-full md:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca azienda, referente o codice…" className="h-10 w-full border border-black/15 bg-white pl-10 pr-3 text-sm placeholder:text-ink/35 focus:border-ink focus:outline-none" data-testid="admin-search-input" />
          </div>
          <button onClick={load} className="flex h-10 w-10 items-center justify-center border border-black/15 bg-white hover:border-ink" aria-label="Aggiorna" data-testid="admin-refresh-button">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
        <div className="border border-black/10 bg-white" data-testid="admin-orders-list">
          {loading && orders.length === 0 ? (
            <div className="space-y-px">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-16 animate-pulse bg-paper" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-20 text-center" data-testid="admin-orders-empty">
              <Inbox className="h-8 w-8 text-ink/25" />
              <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-ink/45">Nessun preventivo</p>
            </div>
          ) : (
            filtered.map((o) => <OrderRow key={o.id} order={o} active={o.id === selectedId} onClick={() => setSelectedId(o.id)} />)
          )}
        </div>

        <div className="hidden border border-black/10 lg:block lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)]">
          {selected ? (
            <OrderDetail order={selected} onUpdated={onUpdated} onClose={() => setSelectedId(null)} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center bg-white px-8 text-center" data-testid="admin-detail-placeholder">
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-ink/45">Seleziona un preventivo</p>
              <p className="mt-2 max-w-xs text-sm text-ink/45">Vedrai referente, articoli, stato e potrai registrare il ricontatto.</p>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 300, damping: 34 }} className="fixed inset-x-0 bottom-0 top-16 z-[60] border-t border-black/10 lg:hidden" data-testid="admin-detail-mobile">
            <OrderDetail order={selected} onUpdated={onUpdated} onClose={() => setSelectedId(null)} />
          </motion.div>
        )}
      </AnimatePresence>
    </AdminShell>
  );
}
